import {
  parseNumberFilter,
  toFilterText,
  toGeoFilterValues,
  toGeoSearch,
  toPeriodFilterValues,
  toPeriodSearch,
} from '@/utils/filters';
import { VERIFY_STATUS } from '../constants/billing.constants';
import type {
  AllCostSearch,
  ApprovalSearch,
  ExpenseApprovalSearch,
  OfflineDcrSearch,
  OfflineDerSearch,
} from '../types/billing.types';
import { getQueueDefaultPeriod } from './period';

type FilterValues = Record<string, string>;
type VerificationSearch = OfflineDcrSearch & OfflineDerSearch;

function toVerificationFilterValues(search: Partial<VerificationSearch>): FilterValues {
  return {
    ...toPeriodFilterValues(search, getQueueDefaultPeriod()),
    reportingMemberId: toFilterText(search.reportingMemberId),
    userId: toFilterText(search.userId),
    ...toGeoFilterValues(search),
    isVerify: toFilterText(search.isVerify ?? VERIFY_STATUS.PENDING),
  };
}

function toVerificationSearch(values: FilterValues) {
  return {
    ...toPeriodSearch(values),
    reportingMemberId: parseNumberFilter(values.reportingMemberId),
    userId: parseNumberFilter(values.userId),
    ...toGeoSearch(values),
    isVerify: parseNumberFilter(values.isVerify) ?? VERIFY_STATUS.PENDING,
  };
}

export function toOfflineDcrFilterValues(search: OfflineDcrSearch): FilterValues {
  return { ...toVerificationFilterValues(search), dcrTypeId: toFilterText(search.dcrTypeId) };
}

export function toOfflineDcrFilterSearch(values: FilterValues): OfflineDcrSearch {
  return { ...toVerificationSearch(values), dcrTypeId: parseNumberFilter(values.dcrTypeId) };
}

export function toOfflineDerFilterValues(search: OfflineDerSearch): FilterValues {
  return {
    ...toVerificationFilterValues(search),
    expenseTypeId: toFilterText(search.expenseTypeId),
    minAmount: toFilterText(search.minAmount),
  };
}

export function toOfflineDerFilterSearch(values: FilterValues): OfflineDerSearch {
  return {
    ...toVerificationSearch(values),
    expenseTypeId: parseNumberFilter(values.expenseTypeId),
    minAmount: parseNumberFilter(values.minAmount),
  };
}

export function toApprovalFilterValues(search: ApprovalSearch): FilterValues {
  return {
    ...toPeriodFilterValues(search, getQueueDefaultPeriod()),
    reportingMemberId: toFilterText(search.reportingMemberId),
    userId: toFilterText(search.userId),
    ...toGeoFilterValues(search),
    designationId: toFilterText(search.designationId),
    dcrTypeId: toFilterText(search.dcrTypeId),
    expenseTypeId: toFilterText(search.expenseTypeId),
    approveTypeNo: toFilterText(search.approveTypeNo),
    minFare: toFilterText(search.minFare),
    minAmount: toFilterText(search.minAmount),
  };
}

export function toApprovalFilterSearch(values: FilterValues): ApprovalSearch {
  return {
    ...toPeriodSearch(values),
    reportingMemberId: parseNumberFilter(values.reportingMemberId),
    userId: parseNumberFilter(values.userId),
    ...toGeoSearch(values),
    designationId: parseNumberFilter(values.designationId),
    dcrTypeId: parseNumberFilter(values.dcrTypeId),
    expenseTypeId: parseNumberFilter(values.expenseTypeId),
    approveTypeNo: parseNumberFilter(values.approveTypeNo),
    minFare: parseNumberFilter(values.minFare),
    minAmount: parseNumberFilter(values.minAmount),
  };
}

export function toAllCostFilterValues(search: AllCostSearch): FilterValues {
  return {
    ...toPeriodFilterValues(search, getQueueDefaultPeriod()),
    ...toGeoFilterValues(search),
    userId: toFilterText(search.userId),
  };
}

export function toAllCostFilterSearch(values: FilterValues): AllCostSearch {
  return {
    ...toPeriodSearch(values),
    ...toGeoSearch(values),
    userId: parseNumberFilter(values.userId),
  };
}

export function toExpenseApprovalFilterValues(search: ExpenseApprovalSearch): FilterValues {
  return {
    ...toPeriodFilterValues(search, getQueueDefaultPeriod()),
    userId: toFilterText(search.userId),
    fiscalYearId: toFilterText(search.fiscalYearId),
    approveTypeNo: toFilterText(search.approveTypeNo),
  };
}

export function toExpenseApprovalFilterSearch(values: FilterValues): ExpenseApprovalSearch {
  return {
    ...toPeriodSearch(values),
    userId: parseNumberFilter(values.userId),
    fiscalYearId: parseNumberFilter(values.fiscalYearId),
    approveTypeNo: parseNumberFilter(values.approveTypeNo) ?? VERIFY_STATUS.PENDING,
  };
}
